"use client";

import React, { createContext, useContext, ReactNode } from "react";
import { useNotifications } from "@/hooks/useNotifications";
import type {
  TicketNotification,
  NotificationType,
} from "@/types/ticketNotifications";

interface NotificationContextValue {
  notifications: TicketNotification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadingMore: boolean;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  createNotification: (
    ticketId: string,
    type: NotificationType,
    note?: string
  ) => Promise<void>;
  refreshNotifications: () => Promise<void>;
  loadMoreNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined
);

interface NotificationProviderProps {
  children: ReactNode;
  enablePolling?: boolean;
  autoMarkAsRead?: boolean;
  loadOnMount?: boolean;
  refreshOnFocus?: boolean;
}

export function NotificationProvider({
  children,
  enablePolling = true,
  autoMarkAsRead = false,
  loadOnMount = true,
  refreshOnFocus,
}: NotificationProviderProps) {
  const notificationData = useNotifications({
    enablePolling,
    autoMarkAsRead,
    loadOnMount,
    refreshOnFocus,
  });

  return (
    <NotificationContext.Provider value={notificationData}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotificationContext must be used within a NotificationProvider"
    );
  }
  return context;
}
